import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Sparkles } from 'lucide-react';

export default function PurchaseTimerWidget() {
    const { publicSettings } = useSelector((s) => s.gateway || {});
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isFuture, setIsFuture] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggingPivot, setIsDraggingPivot] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    // Coords relative to container: x/y for card center, xp for horizontal sliding pivot
    const [coords, setCoords] = useState(() => {
        if (typeof window === 'undefined') return { x: 160, y: 45, xp: 160, angle: 0 };
        const savedXp = parseFloat(localStorage.getItem('purchase_timer_xp'));
        const width = window.innerWidth;
        const safeMin = Math.min(100, width / 2);
        const safeMax = Math.max(safeMin, width - safeMin);
        let initialXp;
        if (isNaN(savedXp)) {
            initialXp = width / 2;
        } else {
            initialXp = Math.max(safeMin, Math.min(safeMax, savedXp));
        }
        return { x: initialXp, y: 45, xp: initialXp, angle: 0 };
    });

    const containerRef = useRef(null);
    const isDraggingRef = useRef(false);
    const isDraggingPivotRef = useRef(false);

    // Initialize refs with saved pivot displacement if available
    const getSavedXp = () => {
        if (typeof window === 'undefined') return 160;
        const savedXp = parseFloat(localStorage.getItem('purchase_timer_xp'));
        const width = window.innerWidth;
        const safeMin = Math.min(100, width / 2);
        const safeMax = Math.max(safeMin, width - safeMin);
        if (isNaN(savedXp)) {
            return width / 2;
        }
        return Math.max(safeMin, Math.min(safeMax, savedXp));
    };
    const initialXp = getSavedXp();

    const xRef = useRef(initialXp);   // Horizontal position (absolute from container left)
    const yRef = useRef(45);   // Vertical position
    const xpRef = useRef(initialXp);   // Pivot horizontal position
    const vxRef = useRef(0);   // Card velocity X
    const vyRef = useRef(0);   // Card velocity Y
    const angleRef = useRef(0);
    const lastTimeRef = useRef(0);
    const grabOffsetRef = useRef({ x: 0, y: 0 });
    const prevWidthRef = useRef(typeof window !== 'undefined' ? window.innerWidth : 1000);

    useEffect(() => {
        const handleResize = () => {
            const prevWidth = prevWidthRef.current || window.innerWidth;
            const newWidth = window.innerWidth;
            prevWidthRef.current = newWidth;

            // Calculate proportional position on screen resize
            const ratio = xpRef.current / prevWidth;
            const newXp = Math.max(40, Math.min(newWidth - 40, newWidth * ratio));

            const dx = xRef.current - xpRef.current;
            xpRef.current = newXp;
            xRef.current = newXp + dx;

            setCoords(prev => ({
                ...prev,
                xp: xpRef.current,
                x: xRef.current
            }));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!publicSettings?.purchasePaused || !publicSettings?.purchaseStartsAt) {
            setIsFuture(false);
            return;
        }

        const targetTime = new Date(publicSettings.purchaseStartsAt).getTime();

        const calculateTime = () => {
            const now = Date.now();
            const difference = targetTime - now;
            if (difference <= 0) {
                setIsFuture(false);
                return;
            }

            setIsFuture(true);
            setTimeLeft({
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            });
        };

        calculateTime();
        const interval = setInterval(calculateTime, 1000);
        return () => clearInterval(interval);
    }, [publicSettings]);

    useEffect(() => {
        const isHomePage = window.location.pathname === '/';
        if (!isHomePage) {
            setIsVisible(true);
            return;
        }

        const handleComplete = () => {
            setIsVisible(true);
        };
        window.addEventListener('heroAnimationComplete', handleComplete);

        // Safety fallback: show widget after 4.5 seconds if animation event didn't fire
        const fallback = setTimeout(() => {
            setIsVisible(true);
        }, 4500);

        return () => {
            window.removeEventListener('heroAnimationComplete', handleComplete);
            clearTimeout(fallback);
        };
    }, []);

    useEffect(() => {
        let animationFrameId;
        let lastTimestamp = performance.now();

        const updatePhysics = (timestamp) => {
            let dt = (timestamp - lastTimestamp) / 1000;
            if (dt > 0.1) dt = 0.1; // Cap dt to avoid issues on tab background
            lastTimestamp = timestamp;

            const x = xRef.current;
            const y = yRef.current;
            const xp = xpRef.current;

            // Vector from pivot (xp, 0) to card (x, y)
            const dx = x - xp;
            const dy = y;

            const d = Math.sqrt(dx * dx + dy * dy) || 0.1;
            const L = 65; // Natural rope length (compacted)

            // Spring constant (stiffness of rope) and damping
            const kSpring = 160.0;
            const damping = 1.6;
            const gravity = 850.0;

            // Tension force acts along the rope towards pivot
            const tensionMagnitude = -kSpring * (d - L);
            const tx = tensionMagnitude * (dx / d);
            const ty = tensionMagnitude * (dy / d);

            if (!isDraggingRef.current) {
                // Card physics: Tension + Gravity + Damping
                const fx = tx - damping * vxRef.current;
                const fy = ty + gravity - damping * vyRef.current;

                // Add slight ambient wind force when resting
                const velocityMagnitude = Math.sqrt(vxRef.current * vxRef.current + vyRef.current * vyRef.current);
                if (velocityMagnitude < 2.0 && Math.abs(d - L) < 1.0) {
                    const wind = 5.0 * Math.sin(timestamp / 700);
                    vxRef.current += wind * dt;
                }

                vxRef.current += fx * dt;
                vyRef.current += fy * dt;

                xRef.current += vxRef.current * dt;
                yRef.current += vyRef.current * dt;
                angleRef.current = Math.atan2(dx, dy) * (180 / Math.PI);
            }

            setCoords({
                x: xRef.current,
                y: yRef.current,
                xp: xpRef.current,
                angle: angleRef.current
            });

            animationFrameId = requestAnimationFrame(updatePhysics);
        };

        animationFrameId = requestAnimationFrame(updatePhysics);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    if (!publicSettings?.purchasePaused) {
        return null;
    }

    const padZero = (num) => String(num).padStart(2, '0');

    const getContainerRelativeCoords = (clientX, clientY) => {
        if (!containerRef.current) return { x: 0, y: 0 };
        const rect = containerRef.current.getBoundingClientRect();
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    // Card drag handlers
    const handlePointerDown = (e) => {
        e.preventDefault();
        isDraggingRef.current = true;
        setIsDragging(true);

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        grabOffsetRef.current = {
            x: p.x - xRef.current,
            y: p.y - yRef.current
        };

        vxRef.current = 0;
        vyRef.current = 0;
        lastTimeRef.current = performance.now();

        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
    };

    const handlePointerMove = (e) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        const targetX = p.x - grabOffsetRef.current.x;
        const targetY = Math.max(15, p.y - grabOffsetRef.current.y); // Prevent dragging above pivot

        const now = performance.now();
        const dt = (now - lastTimeRef.current) / 1000;
        if (dt > 0) {
            vxRef.current = (targetX - xRef.current) / dt;
            vyRef.current = (targetY - yRef.current) / dt;
        }
        lastTimeRef.current = now;

        xRef.current = targetX;
        yRef.current = targetY;

        // Calculate angle from the dynamic sliding pivot
        const dx = targetX - xpRef.current;
        const dy = targetY;
        angleRef.current = Math.atan2(dx, dy) * (180 / Math.PI);
    };

    const handlePointerUp = (e) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        setIsDragging(false);
        e.currentTarget.releasePointerCapture(e.pointerId);
    };

    const handlePointerEnter = (e) => {
        if (isDraggingRef.current || isDraggingPivotRef.current) return;

        // Give it a realistic physical kick on cursor sweep
        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        const kickDir = p.x < xRef.current ? 160 : -160;
        vxRef.current += kickDir;
    };

    // Pivot drag handlers
    const handlePivotDown = (e) => {
        e.preventDefault();
        isDraggingPivotRef.current = true;
        setIsDraggingPivot(true);

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        grabOffsetRef.current = {
            x: p.x - xpRef.current,
            y: 0
        };

        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
    };

    const handlePivotMove = (e) => {
        if (!isDraggingPivotRef.current) return;
        e.preventDefault();

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        const targetX = p.x - grabOffsetRef.current.x;

        xpRef.current = Math.max(40, Math.min(window.innerWidth - 40, targetX));

        // Recalculate card angle based on new pivot X position
        const dx = xRef.current - xpRef.current;
        const dy = yRef.current;
        angleRef.current = Math.atan2(dx, dy) * (180 / Math.PI);
        e.stopPropagation();
    };

    const handlePivotUp = (e) => {
        if (!isDraggingPivotRef.current) return;
        isDraggingPivotRef.current = false;
        setIsDraggingPivot(false);
        e.currentTarget.releasePointerCapture(e.pointerId);

        // Save the drop point
        localStorage.setItem('purchase_timer_xp', xpRef.current.toString());
        e.stopPropagation();
    };

    return (
        <div
            ref={containerRef}
            className="fixed top-0 left-0 w-full pointer-events-none z-[100] select-none transition-all duration-1000 ease-out"
            style={{
                height: '400px',
                top: isVisible ? '0px' : '-400px'
            }}
        >
            <style>{`
                @keyframes widget-marquee {
                    0% { transform: translateX(50%); }
                    100% { transform: translateX(-100%); }
                }
                .widget-marquee-content {
                    display: inline-block;
                    white-space: nowrap;
                    animation: widget-marquee 15s linear infinite;
                }
                @media (min-width: 768px) {
                    .widget-marquee-content {
                        animation: none;
                        white-space: normal;
                        display: block;
                    }
                }
            `}</style>

            {/* SVG Wire/Cord that stretches and rotates dynamically */}
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-20 overflow-visible">
                {/* Horizontal sliding bar guide at the top */}
                <line
                    x1="40"
                    y1="0"
                    x2="100%"
                    y2="0"
                    stroke="#27272a"
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ opacity: 0.3 }}
                />

                {/* Connection screw at the top sliding pivot point */}
                <circle
                    cx={coords.xp}
                    cy="0"
                    r={isDraggingPivot ? 5 : 3.5}
                    fill={isDraggingPivot ? "#a1a1aa" : "#52525b"}
                    stroke={isDraggingPivot ? "#ffffff" : "#a1a1aa"}
                    strokeWidth="1.2"
                    onPointerDown={handlePivotDown}
                    onPointerMove={handlePivotMove}
                    onPointerUp={handlePivotUp}
                    onPointerCancel={handlePivotUp}
                    style={{
                        display: !isDraggingPivot ? "none" : "block",
                        filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.6))',
                        cursor: isDraggingPivot ? 'grabbing' : 'grab',
                        pointerEvents: 'auto'
                    }}
                />

                {/* Metallic core cord */}
                <line
                    x1={coords.xp}
                    y1="0"
                    x2={coords.x}
                    y2={coords.y}
                    stroke="#a1a1aa"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                />

                {/* Dynamic ring connector at the bottom of the wire */}
                <circle
                    cx={coords.x}
                    cy={coords.y}
                    r="4"
                    fill="transparent"
                    stroke="#a1a1aa"
                    strokeWidth="1.2"
                    style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))' }}
                />
            </svg>

            {/* Draggable peg hit target */}
            <div
                onPointerDown={handlePivotDown}
                onPointerMove={handlePivotMove}
                onPointerUp={handlePivotUp}
                onPointerCancel={handlePivotUp}
                className="pointer-events-auto"
                style={{
                    position: 'absolute',
                    left: `${coords.xp}px`,
                    top: '0px',
                    width: '32px',
                    height: '32px',
                    transform: 'translate(-50%, -50%)',
                    cursor: isDraggingPivot ? 'grabbing' : 'grab',
                    pointerEvents: 'auto',
                    zIndex: 9999,
                    touchAction: 'none',
                    borderRadius: '50%'
                }}
            />

            {/* Draggable Card Face */}
            <div
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onPointerEnter={handlePointerEnter}
                style={{
                    position: 'absolute',
                    left: `${coords.x}px`,
                    top: `${coords.y}px`,
                    transform: `translate(-50%, 0) rotate(${coords.angle}deg)`,
                    touchAction: 'none',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    pointerEvents: 'auto'
                }}
                className="relative group scale-[0.6] xs:scale-[0.7] sm:scale-75 md:scale-90 origin-top flex-shrink-0"
            >
                {/* Glow Backdrop */}
                <div className={`absolute inset-0 rounded-xl opacity-60 blur-md transition-all duration-500 group-hover:opacity-100 ${isFuture
                    ? 'bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink shadow-[0_0_20px_rgba(0,240,255,0.4)]'
                    : 'bg-gradient-to-r from-cyber-pink to-cyber-purple shadow-[0_0_20px_rgba(255,0,122,0.4)]'
                    }`} />

                {/* 3D Beveled Card Face */}
                <div className="relative flex items-center gap-2.5 px-3 py-2.5 bg-[#0a0a16]/95 backdrop-blur-xl border border-white/10 rounded-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.15),0_10px_25px_rgba(0,0,0,0.9),0_4px_10px_rgba(0,0,0,0.5)] transform transition-transform duration-500 group-hover:scale-[1.02]">
                    <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-cyber-cyan/50 rounded-tl" />
                    <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-cyber-cyan/50 rounded-tr" />
                    <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-cyber-cyan/50 rounded-bl" />
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-cyber-cyan/50 rounded-br" />

                    <div className="text-center w-[170px] sm:w-[180px] max-w-[180px] h-auto overflow-hidden">
                        {isFuture ? (
                            <>
                                <p className="text-[10px] font-cyber font-bold text-center justify-center tracking-[0.2em] text-cyber-cyan flex items-center gap-1 uppercase">
                                    <Sparkles size={11} className="animate-pulse text-cyber-pink" /> {publicSettings?.purchasePausedHeading || "STORE ACCESS"} <Sparkles size={11} className="animate-pulse text-cyber-pink rotate-90" />
                                </p>
                                <h4 className="text-base sm:text-xl font-cyber font-black tracking-widest text-white mt-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                                    {timeLeft.days > 0 ? (
                                        <>{padZero(timeLeft.days)}d : {padZero(timeLeft.hours)}h : {padZero(timeLeft.minutes)}m</>
                                    ) : (
                                        <>{padZero(timeLeft.hours)}h : {padZero(timeLeft.minutes)}m : {padZero(timeLeft.seconds)}s</>
                                    )}
                                </h4>
                            </>
                        ) : (
                            <>
                                <p className="text-[10px] font-cyber font-bold text-center justify-center tracking-[0.2em] text-cyber-cyan flex items-center gap-1 uppercase">
                                    <Sparkles size={11} className="animate-pulse text-cyber-pink" /> {publicSettings?.purchasePausedHeading || "STORE ACCESS"} <Sparkles size={11} className="animate-pulse text-cyber-pink rotate-90" />
                                </p>
                                <div className="w-full overflow-hidden">
                                    <h4 className="widget-marquee-content text-[12px] md:text-[11px] font-cyber text-cyber-cyan animate-pulse font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-pink uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                                        {publicSettings?.purchasePausedMessage || "We're experiencing technical issues. We'll be back soon"}
                                    </h4>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
